'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ConsentPreferenceCenter from '@/components/ConsentPreferenceCenter';
import { getConsent, saveConsent, detectRegion } from '@/lib/consent';

export default function PrivacyPage() {
  const [showPreferences, setShowPreferences] = useState(false);
  const [cookieToggles, setCookieToggles] = useState({
    analytics: false,
    marketing: false,
    preferences: false,
  });

  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      setCookieToggles({
        analytics: existing.choices.analytics,
        marketing: existing.choices.marketing,
        preferences: existing.choices.preferences,
      });
    }
  }, []);

  const handleToggle = (key: 'analytics' | 'marketing' | 'preferences') => {
    setCookieToggles((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      const region = detectRegion();
      saveConsent(updated, region);
      return updated;
    });
  };

  const lastUpdated = new Date()?.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)', color: 'var(--foreground)' }}>
      <Header />
      <main className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-28">
        {/* Back link */}
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-sm font-medium mb-8 transition-colors min-h-[44px]"
          style={{ color: 'var(--muted-foreground)' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--foreground)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--muted-foreground)')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Zurück zur Startseite
        </Link>

        {/* Page Title */}
        <h1 className="font-display font-semibold text-3xl md:text-5xl mb-4 leading-tight" style={{ color: 'var(--foreground)' }}>
          Datenschutzerklärung
        </h1>
        <p className="text-sm mb-10" style={{ color: 'var(--muted-foreground)' }}>
          Zuletzt aktualisiert: {lastUpdated}
        </p>

        <div className="space-y-8 md:space-y-10 text-base leading-relaxed" style={{ color: 'var(--foreground)' }}>

          {/* 1. What Information We Collect */}
          <section>
            <h2 className="font-display font-semibold text-2xl mb-4" style={{ color: 'var(--foreground)' }}>
              1. Welche Informationen wir erfassen
            </h2>
            <p style={{ color: 'var(--muted-foreground)' }} className="mb-3">
              Wir erfassen verschiedene Arten von Informationen, um unsere Website zu betreiben und unsere Dienstleistungen bereitzustellen:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2" style={{ color: 'var(--muted-foreground)' }}>
              <li><strong>Kontaktinformationen:</strong> Name, E-Mail-Adresse und Telefonnummer, die Sie über unser Kontaktformular bereitstellen</li>
              <li><strong>Nutzungsdaten:</strong> Informationen über Ihre Interaktionen mit unserer Website, einschließlich besuchter Seiten und Verweildauer</li>
              <li><strong>Geräteinformationen:</strong> IP-Adresse, Browsertyp, Betriebssystem und andere technische Informationen</li>
              <li><strong>Cookie-Daten:</strong> Informationen, die durch Cookies und ähnliche Tracking-Technologien erfasst werden</li>
            </ul>
          </section>

          {/* 2. How We Use Your Information */}
          <section>
            <h2 className="font-display font-semibold text-2xl mb-4" style={{ color: 'var(--foreground)' }}>
              2. Wie wir Ihre Informationen verwenden
            </h2>
            <p style={{ color: 'var(--muted-foreground)' }} className="mb-3">
              Wir verwenden die erfassten Informationen für folgende Zwecke:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2" style={{ color: 'var(--muted-foreground)' }}>
              <li><strong>Erbringung von Dienstleistungen:</strong> Beantwortung von Anfragen und Bereitstellung von Buchhaltungsdienstleistungen</li>
              <li><strong>Website-Verbesserung:</strong> Analyse der Website-Nutzung zur Optimierung der Benutzerfreundlichkeit</li>
              <li><strong>Kommunikation:</strong> Kontaktaufnahme bezüglich Ihrer Anfragen oder Updates zu unseren Dienstleistungen</li>
              <li><strong>Rechtliche Compliance:</strong> Einhaltung gesetzlicher Anforderungen und Schutz unserer Rechte</li>
            </ul>
          </section>

          {/* 3. Cookies and Tracking */}
          <section>
            <h2 className="font-display font-semibold text-2xl mb-4" style={{ color: 'var(--foreground)' }}>
              3. Cookies und Tracking-Technologien
            </h2>
            <p style={{ color: 'var(--muted-foreground)' }} className="mb-3">
              Unsere Website verwendet Cookies und ähnliche Tracking-Technologien. Weitere Informationen finden Sie in unserer{' '}
              <Link href="/cookies" className="underline" style={{ color: 'var(--accent)' }}>
                Cookie-Richtlinie
              </Link>
              .
            </p>
            <p style={{ color: 'var(--muted-foreground)' }} className="mb-4">
              Wir verwenden folgende Cookie-Kategorien. Sie können optionale Cookies hier direkt ein- oder ausschalten:
            </p>

            {/* Cookie Categories with Toggles */}
            <div className="space-y-3 mb-6">
              {/* Necessary - always on */}
              <div className="flex items-center justify-between p-4 rounded-lg border gap-4" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--muted)' }}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm" style={{ color: 'var(--foreground)' }}>Notwendige Cookies</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Erforderlich für die Website-Funktionalität</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs font-medium hidden sm:inline" style={{ color: 'var(--muted-foreground)' }}>Immer aktiv</span>
                  <div className="w-11 h-6 rounded-full bg-green-500 flex items-center justify-end px-0.5 cursor-not-allowed opacity-70">
                    <div className="w-5 h-5 rounded-full bg-white shadow-sm" />
                  </div>
                </div>
              </div>

              {/* Präferenzen */}
              <div className="flex items-center justify-between p-4 rounded-lg border gap-4" style={{ borderColor: 'var(--border)' }}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm" style={{ color: 'var(--foreground)' }}>Präferenz-Cookies</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Speichern Ihre Einstellungen (optional)</p>
                </div>
                <button
                  role="switch"
                  aria-checked={cookieToggles.preferences}
                  aria-label="Präferenz-Cookies ein- oder ausschalten"
                  onClick={() => handleToggle('preferences')}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 flex-shrink-0 ${
                    cookieToggles.preferences ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                  style={{ focusRingColor: 'var(--accent)' } as React.CSSProperties}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                      cookieToggles.preferences ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between p-4 rounded-lg border gap-4" style={{ borderColor: 'var(--border)' }}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm" style={{ color: 'var(--foreground)' }}>Marketing-Cookies</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Für personalisierte Werbung (optional)</p>
                </div>
                <button
                  role="switch"
                  aria-checked={cookieToggles.marketing}
                  aria-label="Marketing-Cookies ein- oder ausschalten"
                  onClick={() => handleToggle('marketing')}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 flex-shrink-0 ${
                    cookieToggles.marketing ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                      cookieToggles.marketing ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Analytik */}
              <div className="flex items-center justify-between p-4 rounded-lg border gap-4" style={{ borderColor: 'var(--border)' }}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm" style={{ color: 'var(--foreground)' }}>Analyse-Cookies</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Helfen uns, die Website-Nutzung zu verstehen (optional)</p>
                </div>
                <button
                  role="switch"
                  aria-checked={cookieToggles.analytics}
                  aria-label="Analyse-Cookies ein- oder ausschalten"
                  onClick={() => handleToggle('analytics')}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 flex-shrink-0 ${
                    cookieToggles.analytics ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                      cookieToggles.analytics ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Cookie Settings Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowPreferences(true)}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-medium transition-colors min-h-[48px]"
                style={{ backgroundColor: 'var(--accent)', color: '#fff' }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M5.5 8.5L7 10L10.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Cookie-Einstellungen anpassen
              </button>
              <Link
                href="/cookies"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-medium transition-colors border min-h-[48px]"
                style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                Cookie-Richtlinie lesen
              </Link>
            </div>
          </section>

          {/* 4. Do Not Sell My Personal Information */}
          <section id="do-not-sell">
            <h2 className="font-display font-semibold text-2xl mb-4" style={{ color: 'var(--foreground)' }}>
              4. Meine Daten nicht verkaufen
            </h2>
            <p style={{ color: 'var(--muted-foreground)' }} className="mb-3">
              Wir verkaufen Ihre persönlichen Daten nicht an Dritte. Ihre Privatsphäre ist uns wichtig und wir behandeln Ihre Daten vertraulich.
            </p>
            <p style={{ color: 'var(--muted-foreground)' }}>
              Falls Sie Fragen zu Ihren Datenschutzrechten haben, kontaktieren Sie uns bitte unter{' '}
              <a href="mailto:info@aktas-buchhaltung.de" style={{ color: 'var(--accent)' }}>
                info@aktas-buchhaltung.de
              </a>
              .
            </p>
          </section>

          {/* 5. Your Rights */}
          <section>
            <h2 className="font-display font-semibold text-2xl mb-4" style={{ color: 'var(--foreground)' }}>
              5. Ihre Rechte
            </h2>
            <p style={{ color: 'var(--muted-foreground)' }} className="mb-3">
              Nach der Datenschutz-Grundverordnung (DSGVO) haben Sie folgende Rechte:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2" style={{ color: 'var(--muted-foreground)' }}>
              <li><strong>Auskunftsrecht:</strong> Sie können jederzeit Auskunft über Ihre gespeicherten Daten verlangen</li>
              <li><strong>Berichtigungsrecht:</strong> Sie können fehlerhafte Daten korrigieren lassen</li>
              <li><strong>Löschungsrecht:</strong> Sie können die Löschung Ihrer Daten verlangen ("Recht auf Vergessenwerden")</li>
              <li><strong>Einschränkungsrecht:</strong> Sie können die Verarbeitung Ihrer Daten einschränken</li>
              <li><strong>Datenportabilität:</strong> Sie können Ihre Daten in einem strukturierten Format erhalten</li>
              <li><strong>Widerspruchsrecht:</strong> Sie können der Verarbeitung Ihrer Daten widersprechen</li>
            </ul>
            <p style={{ color: 'var(--muted-foreground)' }} className="mt-3">
              Um diese Rechte auszuüben, kontaktieren Sie uns bitte unter{' '}
              <a href="mailto:info@aktas-buchhaltung.de" style={{ color: 'var(--accent)' }}>
                info@aktas-buchhaltung.de
              </a>
              .
            </p>
          </section>

          {/* 6. Data Retention */}
          <section>
            <h2 className="font-display font-semibold text-2xl mb-4" style={{ color: 'var(--foreground)' }}>
              6. Datenspeicherung
            </h2>
            <p style={{ color: 'var(--muted-foreground)' }} className="mb-3">
              Wir speichern Ihre persönlichen Daten nur so lange, wie es für die Erfüllung der Zwecke erforderlich ist, für die sie erfasst wurden.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2" style={{ color: 'var(--muted-foreground)' }}>
              <li><strong>Kontaktformular-Daten:</strong> Werden nach Beantwortung Ihrer Anfrage gelöscht, sofern keine rechtliche Aufbewahrungspflicht besteht</li>
              <li><strong>Cookie-Einwilligungsdaten:</strong> Werden in Ihrem Browser gespeichert und können jederzeit gelöscht werden</li>
              <li><strong>Nutzungsdaten:</strong> Werden für bis zu 12 Monate gespeichert</li>
            </ul>
          </section>

          {/* 7. Contact Us */}
          <section>
            <h2 className="font-display font-semibold text-2xl mb-4" style={{ color: 'var(--foreground)' }}>
              7. Kontakt
            </h2>
            <p style={{ color: 'var(--muted-foreground)' }} className="mb-3">
              Wenn Sie Fragen zu dieser Datenschutzerklärung oder zu unseren Datenschutzpraktiken haben, kontaktieren Sie uns bitte:
            </p>
            <div className="space-y-2" style={{ color: 'var(--muted-foreground)' }}>
              <p><strong>Buchhaltungsbüro Aktas</strong></p>
              <p>Inhaber: Sulleyman Aktas</p>
              <p>
                E-Mail:{' '}
                <a href="mailto:info@aktas-buchhaltung.de" style={{ color: 'var(--accent)' }}>
                  info@aktas-buchhaltung.de
                </a>
              </p>
              <p>
                Telefon:{' '}
                <a href="tel:+4915223610151" style={{ color: 'var(--accent)' }}>
                  +49 1522 3610151
                </a>
              </p>
            </div>
          </section>

          {/* 8. Last Updated */}
          <section>
            <h2 className="font-display font-semibold text-2xl mb-4" style={{ color: 'var(--foreground)' }}>
              8. Letzte Aktualisierung
            </h2>
            <p style={{ color: 'var(--muted-foreground)' }}>
              Diese Datenschutzerklärung wurde zuletzt am {lastUpdated} aktualisiert. Wir behalten uns das Recht vor, diese Datenschutzerklärung jederzeit zu ändern. Änderungen werden auf dieser Seite veröffentlicht.
            </p>
          </section>

        </div>
      </main>
      <Footer />

      {showPreferences && (
        <ConsentPreferenceCenter
          onClose={() => setShowPreferences(false)}
          onSave={() => {
            setShowPreferences(false);
            // Sync toggles after saving from modal
            const updated = getConsent();
            if (updated) {
              setCookieToggles({
                analytics: updated.choices.analytics,
                marketing: updated.choices.marketing,
                preferences: updated.choices.preferences,
              });
            }
          }}
        />
      )}
    </div>
  );
}
